import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'typeface-roboto';
import { SessionExpiredOverlay } from './components/SessionExpiredOverlay';
import { ConsentGate } from './features/consent/ConsentGate';
import LoginPage from './features/auth/login/components/LoginPage';
import Registration from './features/auth/registration/components/Registration';
import RegistrationInsertCode from './features/auth/registration/components/RegistrationInsertCode';
import RegistrationSuccess from './features/auth/registration/components/RegistrationSuccess';
import ChangePasswordSuccess from './features/auth/resetPassword/pages/ChangePasswordSuccess';
import ForgotEmail from './features/auth/resetPassword/pages/ForgotEmail';
import Chat from './features/chat/components/Chat';
import ControlPanelPage from './features/control-panel/pages/ControlPanelPage';
import Diagnostica from './features/diagnostica/pages/Diagnostica';
import EditQuestionPage from './features/health-assessment/pages/EditQuestionPage';
import HealthAssessment from './features/health-assessment/pages/HealthAssessment';
import HealthAssessmentHomePage from './features/health-assessment/pages/HealthAssessmentHomePage';
import HealthAssessmentSurvey from './features/health-assessment/pages/HealthAssessmentSurvey';
import HealthPlanHistory from './features/health-plan/components/HealthPlanHistory';
import HealthPlanPdfsPage from './features/health-plan/components/HealthPlanPdfsPage';
import OldHealthPlan from './features/health-plan/components/OldHealthPlan';
import BloodRequestPickup from './features/holifya-tests/blood-test/components/BloodRequestPickup';
import DnaTest from './features/holifya-tests/dna-test/components/DnaTest';
import MealPlanHistory from './features/meal-plan/components/MealPlanHistory';
import OldMealPlan from './features/meal-plan/components/OldMealPlan';
import { NavBarElement } from './features/navbar/types/NavBarElement';
import PlatformPageLayout from './features/platform-layout/components/PlatformPageLayout';
import FetchData from './features/post-login/pages/FetchData';
import PharmaciesRedeemSwabcode from './features/post-login/pages/PharmaciesRedeemSwabcode';
import Redeem from './features/post-login/pages/Redeem';
import { Sorting } from './features/post-login/pages/Sorting';
import UploadedForYou from './features/uploads/uploads/UploadedForYou';
import UploadedFromYou from './features/uploads/uploads/UploadedFromYou';
import CheckupAssessmentHomePage from './pages/new-platform/check-up-survey/CheckupAssessmentHomePage';
import CheckupAssessmentSurvey from './pages/new-platform/check-up-survey/CheckupAssessmentSurvey';
import HealthAssessmentSuccessPage from './pages/new-platform/diagnostica/health-assessment/HealthAssessmentSuccessPage';
import ResultsAndProgresses from './pages/new-platform/diagnostica/ResultsAndProgresses';
import TestsAndReports from './pages/new-platform/diagnostica/TestsAndReports';
import MealPlanSupportPdfsPage from './pages/new-platform/health-plan/pages/meal-plan/MealPlanSupportPdfsPage';
import HomePage from './pages/new-platform/HomePage';
import HowItWorksConsults from './pages/new-platform/menu/HowItWorksConsults';
import HowItWorksDna from './pages/new-platform/menu/HowItWorksDna';
import HowItWorksSubHealth from './pages/new-platform/menu/HowItWorksSubHealth';
import HowItWorksSubNutrition from './pages/new-platform/menu/HowItWorksSubNutrition';
import MenuRedeem from './pages/new-platform/menu/MenuRedeem';
import PaymentMethodsAndPurchases from './pages/new-platform/menu/PaymentMethodsAndPurchases';
import PersonalData from './pages/new-platform/menu/PersonalData';
import PurchasePage from './pages/new-platform/menu/PurchasePage';
import './styles/globals.css';
import { forceUpdateDetector } from './utils/forceUpdateDetection';
import { ChatNotificationsInitializer } from './utils/stores/useChatNotificationsStore';
import { useSessionStore } from './utils/stores/useSessionStore';
import { UpdateDetector } from './utils/updateDetection';

import AppointmentsPage from './features/appointments/pages/AppointmentsPage';
import RequireDevAccess from './features/auth/components/RequireDevAccess';
import ForgotPassword from './features/auth/resetPassword/pages/ForgotPassword';
import ForgotPasswordInsertCode from './features/auth/resetPassword/pages/ForgotPasswordInsertCode';
import NewPassword from './features/auth/resetPassword/pages/NewPassword';
import Equipe from './features/equipe/Equipe';
import ExpertsPage from './features/experts/pages/ExpertsPage';
import HealthPlan from './features/health-plan/components/HealthPlan';
import BloodPacking from './features/holifya-tests/blood-test/components/BloodPacking';
import BloodTest from './features/holifya-tests/blood-test/components/BloodTest';
import BloodTutorial from './features/holifya-tests/blood-test/components/BloodTutorial';
import RegisterSample from './features/holifya-tests/blood-test/components/RegisterSample';
import Packing from './features/holifya-tests/dna-test/components/Packing';
import RegisterSwabCode from './features/holifya-tests/dna-test/components/RegisterSwabCode';
import RequestPickup from './features/holifya-tests/dna-test/components/RequestPickup';
import Tutorial from './features/holifya-tests/dna-test/components/Tutorial';
import HolifyaTests from './features/holifya-tests/HolifyaTests';
import MealPlan from './features/meal-plan/components/MealPlan';
import MealPlanPdfsPage from './features/meal-plan/components/MealPlanPdfsPage';
import { Dropzone } from './features/ocr/components/Dropzone';
import ParameterInfoPage from './features/ocr/pages/ParameterInfoPage';
import Parameters from './features/ocr/pages/Parameters';
import Reports from './features/ocr/pages/Reports';
import SingleReport from './features/ocr/pages/SingleReport';
import { ElementImportance } from './features/platform-layout/types/ElementImportance';
import CheckPurchases from './features/post-login/pages/CheckPurchases';
import FetchUserData from './features/post-login/pages/FetchUserData';
import TestPage from './features/test/pages/TestPage';
import TestTMPage from './features/test/pages/TestTMPage';

import { useEffect } from 'react';
import FloatingBloodTestBanner from './components/FloatingBloodTestBanner';
import VersionIndicator from './components/VersionIndicator';
import VersionPopup from './components/VersionPopup';
import { AdminDashboard } from './features/admin';
import HealthAssessmentCategory from './features/health-assessment/pages/HealthAssessmentCategory';
import { VenereAccountPage, VenereHome } from './features/venere';
import VenereChatPage from './features/venere/features/chat/pages/VenereChatPage';
import VenereChatScreen from './features/venere/features/chat/pages/VenereChatScreen';
import VenereConsults from './features/venere/features/consults/pages/VenereConsults';
import { VenereFolders } from './features/venere/features/folders/pages/VenereFolders';
import VenereRegisterWeight from './features/venere/features/weight/pages/VenereRegisterWeight';
import { useVersionTrigger } from './hooks/useVersionTrigger';

const App: React.FC = () => {
  const queryClient = new QueryClient();
  const { isSessionExpired, clearSessionExpired } = useSessionStore();
  const { isVersionPopupOpen, closeVersionPopup, handleVersionIndicatorTap } = useVersionTrigger();

  useEffect(() => {
    // Clear session expiry state on app initialization
    clearSessionExpired();

    // Start update monitoring (only in production)
    if (process.env.NODE_ENV === 'production') {
      const updateDetector = UpdateDetector.getInstance();
      updateDetector.startMonitoring();

      // Cleanup on unmount
      return () => {
        updateDetector.stopMonitoring();
        forceUpdateDetector.stop();
      };
    }
  }, [clearSessionExpired]);

  return (
    <QueryClientProvider client={queryClient}>
      <ChatNotificationsInitializer />
      <SessionExpiredOverlay isVisible={isSessionExpired} />
      <ConsentGate />
      <FloatingBloodTestBanner />
      <Routes>
        <Route path='/' element={<LoginPage />} />
        <Route path='login' element={<LoginPage />} />
        <Route path='forgot-password' element={<ForgotPassword />} />
        <Route path='forgot-email' element={<ForgotEmail />} />
        <Route
          path='forgot-password-insert-code'
          element={<ForgotPasswordInsertCode />}
        />
        <Route
          path='change-password-success'
          element={<ChangePasswordSuccess />}
        />
        <Route path='redeem' element={<Redeem />} />
        <Route
          path='pharmacies-redeem-swabcode'
          element={<PharmaciesRedeemSwabcode />}
        />
        <Route path='sorting' element={<Sorting />} />
        <Route path='/test' element={<TestPage />} />
        <Route path='/test-tm' element={
          <RequireDevAccess>
            <TestTMPage />
          </RequireDevAccess>
        } />
        <Route
          path='forgot-password-insert-code'
          element={<ForgotPasswordInsertCode />}
        />
        <Route path='new-password' element={<NewPassword />} />
        <Route path='email-registration' element={<Registration />} />
        <Route
          path='email-registration-code'
          element={<RegistrationInsertCode />}
        />
        <Route path='registration-success' element={<RegistrationSuccess />} />
        <Route path='check-purchases' element={<CheckPurchases />} />
        <Route path='fetch-user-data' element={<FetchUserData />} />
        <Route path='fetch-data' element={<FetchData />} />
        <Route
          path='change-password-success'
          element={<ChangePasswordSuccess />}
        />
        <Route path='redeem' element={<Redeem />} />
        <Route
          path='pharmacies-redeem-swabcode'
          element={<PharmaciesRedeemSwabcode />}
        />
        <Route
          path='home'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Home}
              importance={ElementImportance.Main}
            >
              <HomePage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='control-panel-page'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Home}
              hideOnMobile={true}
            >
              <ControlPanelPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='chat'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Chat}
              importance={ElementImportance.NavBarComponent}
              title='Chat'
            >
              <Chat />
            </PlatformPageLayout>
          }
        >
          {/* <Route path=':chatSessionId' element={
            <PlatformPageLayout
              currentItem={NavBarElement.Menu}
              hideOnMobile={true}
            >
              <Chat />
            </PlatformPageLayout>
          }
          /> */}
        </Route>
        <Route
          path='chat-session'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Chat}
              importance={ElementImportance.NoNavBarComponent}
              hideOnMobile={true}
            >
              <Chat />
            </PlatformPageLayout>
          }
        >
          <Route path=':chatSessionId' element={<Chat />} />
        </Route>
        <Route
          path='health-plan'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarComponent}
              title='Health Plan'
            >
              <HealthPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan-history'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              title='Storico Health Plan'
              backTo='/health-plan'
              backButtonText='Torna a Health Plan'
            >
              <HealthPlanHistory />
            </PlatformPageLayout>
          }
        />
        <Route
          path='old-health-plan'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              title='Storico Health Plan'
              backTo='/health-plan-history'
              backButtonText='Torna a Storico Health Plan'
            >
              <OldHealthPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan/pdfs'
          element={
            <PlatformPageLayout importance={ElementImportance.NavBarSubComponent} backButtonText='Torna a Health Plan' backTo='/health-plan' title={'Prescrizioni'} currentItem={NavBarElement.HealthPlan}>
              <HealthPlanPdfsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan/meal-plan/pdfs'
          element={
            <PlatformPageLayout importance={ElementImportance.NavBarSubComponent} backButtonText='Torna a Health Plan' backTo='/health-plan' title={'Dieta'} currentItem={NavBarElement.HealthPlan}>
              <MealPlanPdfsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='meal-plan/pdfs'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              backTo='/meal-plan'
              backButtonText='Torna a Meal Plan'
            >
              <MealPlanPdfsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='meal-plan/pdfs/support'
          element={
            <PlatformPageLayout currentItem={NavBarElement.HealthPlan}>
              <MealPlanSupportPdfsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan/meal-plan'
          element={
            <PlatformPageLayout currentItem={NavBarElement.HealthPlan}>
              <MealPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='meal-plan'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              title='Meal Plan'
              backTo='/health-plan'
              backButtonText='Torna a Health Plan'
            >
              <MealPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='meal-plan-history'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              title='Meal Plan passati'
              backTo='/meal-plan'
              backButtonText='Torna a Meal Plan'
            >
              <MealPlanHistory />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan/meal-plan-history'
          element={
            <PlatformPageLayout currentItem={NavBarElement.HealthPlan}>
              <MealPlanHistory />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-plan/old-meal-plan'
          element={
            <PlatformPageLayout currentItem={NavBarElement.HealthPlan}>
              <OldMealPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='old-meal-plan'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.HealthPlan}
              importance={ElementImportance.NavBarSubComponent}
              title='Meal Plan Passati'
              backTo='/meal-plan-history'
              backButtonText='Torna a Meal Plan passati'
            >
              <OldMealPlan />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarComponent}
              title='Diagnostica'
            >
              <Diagnostica />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/results-and-progresses'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <ResultsAndProgresses />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <TestsAndReports />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/dna-test'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <DnaTest />
            </PlatformPageLayout>
          }
        />
        <Route
          path='dna-test'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='DNA Test'
              backTo='/holifya-tests'
              backButtonText='Torna a Test di laboratorio Holifya'
            >
              <DnaTest />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/dna-test/tutorial'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <Tutorial />
            </PlatformPageLayout>
          }
        />
        <Route
          path='dna-test/tutorial'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NoNavBarComponent}
              title="Effettua l'autoprelievo per il test del DNA"
              backTo='/dna-test'
              backButtonText='Torna a DNA Test'
            >
              <Tutorial />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/dna-test/register-swab-code'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RegisterSwabCode />
            </PlatformPageLayout>
          }
        />
        <Route
          path='dna-test/register-swab-code'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RegisterSwabCode />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/dna-test/packing'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <Packing />
            </PlatformPageLayout>
          }
        />
        <Route
          path='dna-test/packing'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <Packing />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/dna-test/request-pickup'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RequestPickup />
            </PlatformPageLayout>
          }
        />
        <Route
          path='dna-test/request-pickup'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RequestPickup />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/blood-test'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodTest />
            </PlatformPageLayout>
          }
        />
        <Route
          path='blood-test'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Analisi degli Aminoacidi'
              backTo='/holifya-tests'
              backButtonText='Torna a Test di laboratorio Holifya'
            >
              <BloodTest />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/blood-test/tutorial'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodTutorial />
            </PlatformPageLayout>
          }
        />
        <Route
          path='blood-test/tutorial'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodTutorial />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/blood-test/sample-register'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RegisterSample />
            </PlatformPageLayout>
          }
        />
        <Route
          path='blood-test/sample-register'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <RegisterSample />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/blood-test/packing'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodPacking />
            </PlatformPageLayout>
          }
        />
        <Route
          path='blood-test/packing'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodPacking />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/blood-test/request-pickup'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodRequestPickup />
            </PlatformPageLayout>
          }
        />
        <Route
          path='blood-test/request-pickup'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <BloodRequestPickup />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/uploaded-for-you'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <UploadedForYou />
            </PlatformPageLayout>
          }
        />
        <Route
          path='diagnostica/tests-and-reports/uploaded-from-you'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <UploadedFromYou />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-assessment'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Health Assessmnet'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <HealthAssessment />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-assessment/category/:category'
          element={
            <PlatformPageLayout currentItem={NavBarElement.Diagnostica}>
              <HealthAssessmentCategory />
            </PlatformPageLayout>
          }
        />
        <Route
          path='equipe'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Equipe}
              importance={ElementImportance.NavBarComponent}
              title='Equipe Medica'
            >
              <Equipe />
            </PlatformPageLayout>
          }
        />
        <Route
          path='equipe/your-appointments'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Equipe}
              importance={ElementImportance.NavBarSubComponent}
              title='I tuoi appuntamenti'
              backTo='/equipe'
              backButtonText='Torna a Equipe Medica'
            >
              <AppointmentsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='equipe/your-experts'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Equipe}
              importance={ElementImportance.NavBarSubComponent}
              title='I tuoi Professionisti'
              backTo='/equipe'
              backButtonText='Torna a Equipe Medica'
            >
              <ExpertsPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='health-assessment-home-page'
          element={<HealthAssessmentHomePage />}
        />
        <Route
          path='health-assessment-survey'
          element={<HealthAssessmentSurvey />}
        />
        <Route
          path='health-assessment-success-page'
          element={<HealthAssessmentSuccessPage />}
        />
        <Route
          path='check-up-assessment-home-page'
          element={<CheckupAssessmentHomePage />}
        />
        <Route
          path='check-up-assessment-survey'
          element={<CheckupAssessmentSurvey />}
        />
        <Route path='edit-question' element={<EditQuestionPage />} />
        <Route
          path='health-assessment-home-page'
          element={<HealthAssessmentHomePage />}
        />
        <Route
          path='health-assessment-success-page'
          element={<HealthAssessmentSuccessPage />}
        />
        <Route
          path='check-up-assessment-home-page'
          element={<CheckupAssessmentHomePage />}
        />
        <Route
          path='check-up-assessment-survey'
          element={<CheckupAssessmentSurvey />}
        />
        <Route path='edit-question' element={<EditQuestionPage />} />
        <Route
          path='menu/personal-data'
          element={
            <PlatformPageLayout importance={ElementImportance.NavBarComponent} title='Dati personali' currentItem={NavBarElement.Menu}>
              <PersonalData />
            </PlatformPageLayout>
          }
        />
        <Route
          path='menu/payment-methods-and-purchases'
          element={
            <PlatformPageLayout importance={ElementImportance.NavBarComponent} title='Acquisti e metodi di pagamento' currentItem={NavBarElement.Menu}>
              <PaymentMethodsAndPurchases />
            </PlatformPageLayout>
          }
        />
        <Route
          path='menu/payment-methods-and-purchases/redeem'
          element={<MenuRedeem />}
        />
        <Route
          path='menu/payment-methods-and-purchases/redeem'
          element={<MenuRedeem />}
        />
        <Route
          path='purchase'
          element={
            <PlatformPageLayout importance={ElementImportance.NavBarComponent} title='Acquista' currentItem={NavBarElement.Menu}>
              <PurchasePage />
            </PlatformPageLayout>
          }
        />
        <Route path='how-it-works-dna' element={<HowItWorksDna />} />
        <Route
          path='how-it-works-sub-health'
          element={<HowItWorksSubHealth />}
        />
        <Route
          path='how-it-works-sub-nutrition'
          element={<HowItWorksSubNutrition />}
        />
        <Route path='how-it-works-consults' element={<HowItWorksConsults />} />
        {/* <Route index element={<TopPage />} /> */}
        <Route
          path='reports'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='I tuoi Referti'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <Reports />
            </PlatformPageLayout>
          }
        />
        <Route
          path='reports/document'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              backTo='/reports'
              backButtonText='Torna a i tuoi Referti'
            >
              <SingleReport />
            </PlatformPageLayout>
          }
        />
        <Route
          path='reports/upload'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NoNavBarComponent}
            >
              <div className='w-full h-full pb-[20px]'>
                <Dropzone />
              </div>
              {/* <div className='md:hidden w-full h-full p-10'>
                <UploadArea />
              </div> */}
            </PlatformPageLayout>
          }
        />
        <Route
          path='parameters'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Andamento parametri'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <Parameters />
            </PlatformPageLayout>
          }
        />
        <Route
          path='parameter-info'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              backButtonText='Torna Indietro'
            >
              <ParameterInfoPage />
            </PlatformPageLayout>
          }
        />
        <Route
          path='uploaded-from-user'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Caricati da te'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <UploadedFromYou />
            </PlatformPageLayout>
          }
        />
        <Route
          path='uploaded-from-expert'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Caricati per te'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <UploadedForYou />
            </PlatformPageLayout>
          }
        />
        <Route
          path='holifya-tests'
          element={
            <PlatformPageLayout
              currentItem={NavBarElement.Diagnostica}
              importance={ElementImportance.NavBarSubComponent}
              title='Test di laboratorio Holifya'
              backTo='/diagnostica'
              backButtonText='Torna a Diagnostica'
            >
              <HolifyaTests />
            </PlatformPageLayout>
          }
        />

        {/* Admin Dashboard Route */}
        <Route
          path='admin/dashboard'
          element={<AdminDashboard />}
        />

        {/* Venere Platform Routes */}
        <Route
          path='venere/home'
          element={<VenereHome />}
        />
        <Route
          path='venere/folders'
          element={<VenereFolders />}
        />
        <Route
          path='venere/chat'
          element={<VenereChatPage />}
        />
        <Route
          path='venere/chat/:chatSessionId'
          element={<VenereChatScreen />}
        />
        <Route
          path='venere/consults'
          element={<VenereConsults />}
        />
        <Route
          path='venere/account'
          element={<VenereAccountPage />}
        />
        <Route
          path='venere/register-weight'
          element={<VenereRegisterWeight />}
        />
        <Route path='*' element={<p>Page Not Found</p>} />
      </Routes>

      <ToastContainer
        position='top-right'
        autoClose={500}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme='light'
      />

      <VersionPopup isOpen={isVersionPopupOpen} onClose={closeVersionPopup} />
      <VersionIndicator onTap={handleVersionIndicatorTap} />
    </QueryClientProvider>
  );
};

export default App;
